import {
  TASK_DESCRIPTION_MAX,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  MANUAL_TASK_TYPES,
  TASK_TYPE_LABELS,
  type ManualTaskType,
  type NoteSource,
  type RecordEntity,
  type TaskStatus,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Loader2,
  Maximize2,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Square,
  TextAlignStart,
  Trash2,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type NoteView, type TaskView } from '../../lib/client.ts';
import { useSession } from '../../lib/session.tsx';
import { NoteDetailDialog } from '../notes/note-detail-dialog.tsx';
import {
  NOTE_SOURCE_KIND,
  passesNoteFilter,
  type NoteFilter,
} from '../notes/note-origin-filter.tsx';
import {
  AutomationBadge,
  NO_TYPE,
  runPending,
  STATUS_BADGE,
  statusLocked,
  TaskDetailDialog,
} from '../tasks/task-detail-dialog.tsx';
import { TaskKindSelect, type TaskKind } from '../tasks/task-kind-select.tsx';
import { TaskRecordLink } from '../tasks/task-record-link.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Markdown } from '../ui/markdown.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Textarea } from '../ui/input.tsx';

export const notesQuery = (entityId: string, entityType: RecordEntity = 'candidates') =>
  ['notes', entityType, entityId] as const;
export const tasksQuery = (entityId: string, entityType: RecordEntity = 'candidates') =>
  ['tasks', entityType, entityId] as const;
export const MY_TASKS_QUERY = ['tasks', 'mine'] as const;

/**
 * A note is "long" past 3 lines or 300 characters: it then opens collapsed to `line-clamp-3`
 * with "Read more" inline after the ellipsis (TS-1873). The card also measures its clamped body, so a
 * short text that still wraps past 3 lines on a narrow card gets the link too.
 */
const LONG_NOTE_CHARS = 300;
const LONG_NOTE_LINES = 3;
const isLongNote = (body: string) =>
  body.length > LONG_NOTE_CHARS || body.split('\n').length > LONG_NOTE_LINES;

/**
 * A record's notes, pinned first, for any record kind (`entityType` + `entityId`; RELAY-945).
 * Access follows the record: without write access the composers are hidden and the API would
 * refuse anyway. With `TasksSection` below, this was the record sidebar beside the candidate
 * and project pages; both are now TABS of those pages (after Overview), each mounted only while
 * open, so its queries run when someone looks. The file keeps its old name.
 *
 * `sources` (a project: `GET /notes/sources`) also lists the notes on its related records — its
 * company, contacts, deals, jobs and shortlisted candidates — each badged with where it is, and
 * `filter` narrows it to some kinds or records (`NoteOriginFilter`). Pin, edit and delete on a
 * related note follow that record's write access. Without `composer` the inline form is gone:
 * the project's card adds notes through `NoteCreateDialog`.
 */

export function NotesSection({
  entityType,
  entityId,
  canWrite,
  sources,
  filter = {},
  composer = true,
  listClassName = '',
  openable = true,
}: {
  entityType: RecordEntity;
  entityId: string;
  canWrite: boolean;
  sources?: readonly NoteSource[] | undefined;
  filter?: NoteFilter;
  composer?: boolean;
  /** Per-note Maximize button; the project tabs turn it off (their card has "Full screen"). */
  openable?: boolean;
  /** Extra classes for the list, e.g. a height cap that scrolls (the project's Overview). */
  listClassName?: string;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const includeRelated = sources !== undefined;
  const notes = useQuery({
    queryKey: [...notesQuery(entityId, entityType), includeRelated ? 'related' : 'own'],
    queryFn: () => api.notes.list(entityType, entityId, { includeRelated }),
  });
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<NoteView | null>(null);
  // Every notes list, not only this record's: a job's note also shows on its project's Notes
  // tab (and the other way round), and only the lists on screen are re-read.
  const refresh = () => qc.invalidateQueries({ queryKey: ['notes'] });
  const create = useMutation({
    mutationFn: () => api.notes.create({ entityType, entityId, body, pinned }),
    onSuccess: async () => {
      setBody('');
      setPinned(false);
      setError(null);
      await refresh();
      await qc.invalidateQueries({ queryKey: ['candidate-history', entityId] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const pin = useMutation({
    mutationFn: (n: NoteView) => api.notes.patch(n.id, { pinned: !n.pinned }),
    onSuccess: refresh,
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (n: NoteView) => api.notes.remove(n.id),
    onSuccess: refresh,
    onError: (err) => setError(describeError(err)),
  });
  // The dialog shows the note it was opened with; after a write it re-reads from the list so
  // a pin or an edit made inside it is reflected without closing.
  const opened = open ? (notes.data?.find((n) => n.id === open.id) ?? open) : null;
  const isOwn = (n: NoteView) => n.entityType === entityType && n.entityId === entityId;
  const sourceOf = (n: NoteView) =>
    sources?.find((src) => src.entityType === n.entityType && src.entityId === n.entityId);
  const sourceLabelOf = (n: NoteView) =>
    isOwn(n)
      ? undefined
      : `${NOTE_SOURCE_KIND[n.entityType]}: ${sourceOf(n)?.label ?? 'no longer linked'}`;
  const writable = (n: NoteView) => (isOwn(n) ? canWrite : (sourceOf(n)?.canWrite ?? false));
  const shown = notes.data?.filter((n) => passesNoteFilter(filter, n.entityType, n.entityId));

  return (
    <section data-testid="notes-section">
      {canWrite && composer && (
        <form
          className="mb-3 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) create.mutate();
          }}
        >
          <Textarea
            aria-label="New note"
            placeholder="Add a note… Markdown or HTML: **bold**, <b>bold</b>, lists, links"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            // The field grows with its text (`field-sizing-content`) but stops at `max-h-40`
            // and scrolls inside, so a long draft never stretches the sidebar (TS-1873).
            className="max-h-40 min-h-20 overflow-y-auto"
          />
          <div className="flex items-center justify-between gap-2">
            <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                aria-label="Pin this note"
              />
              Pin to top
            </label>
            <Button
              type="submit"
              size="sm"
              disabled={!body.trim() || create.isPending}
              data-testid="add-note"
            >
              {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />} Add note
            </Button>
          </div>
        </form>
      )}
      {error && (
        <p className="text-danger mb-2 text-sm" role="alert">
          {error}
        </p>
      )}
      {notes.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {shown?.length === 0 && <p className="text-muted-foreground text-sm">No notes yet.</p>}
      <ol className={`flex flex-col gap-2 ${listClassName}`} data-testid="notes-list">
        {shown?.map((n) => (
          <NoteCard
            key={n.id}
            note={n}
            sourceLabel={sourceLabelOf(n)}
            canPin={writable(n)}
            canEdit={writable(n) && n.author?.id === session.me.user.id}
            isAuthor={n.author?.id === session.me.user.id}
            onPin={() => pin.mutate(n)}
            onDelete={() => remove.mutate(n)}
            onOpen={openable ? () => setOpen(n) : undefined}
          />
        ))}
      </ol>
      {opened && (
        <NoteDetailDialog
          note={opened}
          canEdit={writable(opened) && opened.author?.id === session.me.user.id}
          canDelete={opened.author?.id === session.me.user.id}
          canPin={writable(opened)}
          sourceLabel={sourceLabelOf(opened)}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

/**
 * One note card (TS-1873): a long body opens collapsed to three lines with "Read more"
 * inline after the ellipsis, which expands it in place ("Show less" folds it back); the Maximize button
 * opens the note in `NoteDetailDialog`. Pin, edit (the author's, in place) and delete stay on
 * the card for the quick path.
 */
const READ_MORE = 'Read more';

type Fit =
  | { phase: 'measure' }
  | { phase: 'search'; lo: number; hi: number; cut: number }
  | { phase: 'done'; cut: number | null };

function NoteCard({
  note: n,
  sourceLabel,
  canPin,
  canEdit,
  isAuthor,
  onPin,
  onDelete,
  onOpen,
}: {
  note: NoteView;
  sourceLabel: string | undefined;
  canPin: boolean;
  canEdit: boolean;
  isAuthor: boolean;
  onPin: () => void;
  onDelete: () => void;
  onOpen: (() => void) | undefined;
}) {
  const qc = useQueryClient();
  const bodyRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(n.body);
  const long = isLongNote(n.body) || overflows;
  const collapsed = long && !expanded;
  // Collapsed: the body is cut at the longest text that fits three lines WITH "… Read more" after
  // it (a binary search over the Markdown source, one measured render per step: React does not
  // flush a render started inside a layout effect, so each step is its own effect run), because a
  // CSS clamp puts its ellipsis where the browser decides. The real button is then laid over
  // those last words so it sits right against the ellipsis. First the full body is measured: one
  // that fits needs no button.
  const [fit, setFit] = useState<Fit>({ phase: 'measure' });
  const cut = fit.phase === 'measure' ? null : fit.cut;
  const [pos, setPos] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  useLayoutEffect(() => {
    setFit({ phase: 'measure' });
  }, [n.body, expanded, editing]);
  useLayoutEffect(() => {
    const box = wrapRef.current;
    if (!box || expanded || editing) return;
    let width = box.clientWidth;
    const ro =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (box.clientWidth === width) return;
            width = box.clientWidth;
            setFit({ phase: 'measure' });
          });
    ro?.observe(box);
    return () => ro?.disconnect();
  }, [expanded, editing]);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    const box = wrapRef.current;
    if (!el || !box || expanded || editing) return;
    const fits = () => el.scrollHeight <= el.clientHeight + 1;
    if (fit.phase === 'measure') {
      const over = !fits();
      setOverflows(over);
      if (!over) {
        setPos(null);
        setFit({ phase: 'done', cut: null });
      } else {
        const hi = Math.min(n.body.length, 1500);
        setFit({ phase: 'search', lo: 0, hi, cut: (hi + 1) >> 1 });
      }
    } else if (fit.phase === 'search') {
      const lo = fits() ? fit.cut : fit.lo;
      const hi = fits() ? fit.hi : fit.cut - 1;
      if (lo >= hi) setFit({ phase: 'done', cut: lo });
      else setFit({ phase: 'search', lo, hi, cut: (lo + hi + 1) >> 1 });
    } else if (fit.cut !== null) {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let last: Node | null = null;
      for (let t = walker.nextNode(); t; t = walker.nextNode()) last = t;
      const len = last?.textContent?.length ?? 0;
      if (!last || len < READ_MORE.length || typeof Range === 'undefined') return setPos(null);
      const range = document.createRange();
      range.setStart(last, len - READ_MORE.length);
      range.setEnd(last, len);
      const r = range.getBoundingClientRect?.();
      const o = box.getBoundingClientRect();
      setPos(
        r && r.width > 0
          ? { left: r.left - o.left, top: r.top - o.top, width: r.width, height: r.height }
          : null,
      );
    }
  }, [fit, expanded, editing, n.body.length]);
  const save = useMutation({
    mutationFn: () => api.notes.patch(n.id, { body: draft.trim() }),
    onSuccess: async () => {
      setEditing(false);
      await qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
  return (
    <li
      className={`rounded-xl border p-3 ${n.pinned ? 'border-brand/40 bg-brand-soft/60' : 'border-border bg-card'}`}
      data-testid="note"
      data-pinned={n.pinned || undefined}
      data-collapsed={collapsed || undefined}
    >
      <div className="text-muted-foreground mb-1 flex items-center gap-2 text-xs">
        {n.pinned && <Pin className="text-brand size-3" aria-label="Pinned" />}
        {sourceLabel && (
          <Badge variant="secondary" data-testid="note-source-badge">
            {sourceLabel}
          </Badge>
        )}
        <span className="text-foreground font-medium">
          {n.author?.name ??
            (n.createdByAutomation
              ? n.createdByAutomation.name
                ? `Automation "${n.createdByAutomation.name}"`
                : 'Deleted automation'
              : 'Former member')}
        </span>
        <time dateTime={n.createdAt}>{new Date(n.createdAt).toLocaleString()}</time>
        {n.editedAt && <span>(edited)</span>}
        <span className="ml-auto flex gap-1">
          {onOpen && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="Open note"
              onClick={onOpen}
              data-testid="note-open"
            >
              <Maximize2 />
            </Button>
          )}
          {canPin && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={n.pinned ? 'Unpin note' : 'Pin note'}
              onClick={onPin}
            >
              {n.pinned ? <PinOff /> : <Pin />}
            </Button>
          )}
          {canEdit && !editing && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="Edit note"
              onClick={() => {
                setDraft(n.body);
                setEditing(true);
              }}
              data-testid="note-edit"
            >
              <Pencil />
            </Button>
          )}
          {isAuthor && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="Delete note"
              onClick={onDelete}
            >
              <Trash2 />
            </Button>
          )}
        </span>
      </div>
      {editing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) save.mutate();
          }}
        >
          <Textarea
            autoFocus
            aria-label="Edit note"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="max-h-60 min-h-20 overflow-y-auto"
          />
          {save.error && (
            <p className="text-danger text-sm" role="alert">
              {describeError(save.error)}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!draft.trim() || draft.trim() === n.body || save.isPending}
              data-testid="note-save"
            >
              {save.isPending && <Loader2 className="animate-spin" />} Save
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="relative" ref={wrapRef}>
            <div
              ref={bodyRef}
              className={expanded ? undefined : 'line-clamp-3'}
              data-testid="note-body"
            >
              <Markdown html>
                {collapsed && cut !== null
                  ? `${n.body.slice(0, cut).trimEnd()}… ${READ_MORE}`
                  : n.body}
              </Markdown>
            </div>
            {collapsed && (
              <button
                type="button"
                className={`text-brand text-sm font-medium hover:underline ${
                  pos
                    ? `absolute whitespace-nowrap ${n.pinned ? 'bg-brand-soft' : 'bg-card'}`
                    : 'mt-1 block'
                }`}
                style={pos ?? undefined}
                aria-expanded={false}
                onClick={() => setExpanded(true)}
                data-testid="note-toggle"
              >
                {READ_MORE}
              </button>
            )}
          </div>
          {long && expanded && (
            <button
              type="button"
              className="text-brand mt-1 block text-xs font-medium hover:underline"
              aria-expanded
              onClick={() => setExpanded(false)}
              data-testid="note-toggle"
            >
              Show less
            </button>
          )}
        </>
      )}
    </li>
  );
}

export function TasksSection({
  entityType,
  entityId,
  canWrite,
  listClassName = '',
  kind = 'all',
  onKindChange,
}: {
  entityType: RecordEntity;
  entityId: string;
  canWrite: boolean;
  /** Extra classes for the list, as on `NotesSection`. */
  listClassName?: string;
  /** Filtered in the browser over the record's one task list; the select shows with `onKindChange`. */
  kind?: TaskKind;
  onKindChange?: ((kind: TaskKind) => void) | undefined;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const tasks = useQuery({
    queryKey: tasksQuery(entityId, entityType),
    queryFn: () => api.tasks.list(entityType, entityId),
  });
  const members = useQuery({
    queryKey: ['users', session.me.organisation.id],
    queryFn: () => api.users(),
    staleTime: 60_000,
    enabled: canWrite,
  });
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [due, setDue] = useState('');
  const [type, setType] = useState<ManualTaskType | null>(null);
  const [assignee, setAssignee] = useState(session.me.user.id);
  const [error, setError] = useState<string | null>(null);
  // Every task list (My Tasks, Team Tasks, other records) shares the `tasks` prefix.
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });
  const create = useMutation({
    mutationFn: () =>
      api.tasks.create({
        entityType,
        entityId,
        title,
        assigneeId: assignee,
        ...(type ? { type } : {}),
        ...(description.trim() ? { description } : {}),
        ...(due ? { dueAt: new Date(`${due}T17:00:00`).toISOString() } : {}),
      }),
    onSuccess: async () => {
      setTitle('');
      setDescription('');
      setDue('');
      setType(null);
      setError(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });
  const setStatus = useMutation({
    mutationFn: ({ task, status }: { task: TaskView; status: TaskStatus }) =>
      api.tasks.patch(task.id, { status }),
    onSuccess: refresh,
    onError: (err) => setError(describeError(err)),
  });
  const active = (members.data ?? []).filter((m) => m.status === 'active');
  const shown = tasks.data?.filter((t) => kind === 'all' || t.automated === (kind === 'automated'));

  return (
    <section data-testid="tasks-section">
      {onKindChange && (
        <div className="mb-3 flex justify-end">
          <TaskKindSelect value={kind} onChange={onKindChange} />
        </div>
      )}
      {/* Automated tasks are made by pipeline automations, never by hand. */}
      {canWrite && kind !== 'automated' && (
        <form
          className="mb-3 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) create.mutate();
          }}
        >
          <Input
            aria-label="New task"
            placeholder="Add a task…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            aria-label="New task description"
            placeholder="Description (optional). Markdown works"
            value={description}
            maxLength={TASK_DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="max-h-32 min-h-0 overflow-y-auto"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Due date"
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className="w-40"
            />
            <Select
              value={type ?? NO_TYPE}
              onValueChange={(v) => setType(v === NO_TYPE ? null : (v as ManualTaskType))}
            >
              <SelectTrigger className="w-40" aria-label="Type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TYPE}>No type</SelectItem>
                {MANUAL_TASK_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TASK_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="w-44" aria-label="Assignee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={session.me.user.id}>Me</SelectItem>
                {active
                  .filter((m) => m.id !== session.me.user.id)
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              size="sm"
              className="ml-auto"
              disabled={!title.trim() || create.isPending}
              data-testid="add-task"
            >
              <Plus /> Add task
            </Button>
          </div>
        </form>
      )}
      {error && (
        <p className="text-danger mb-2 text-sm" role="alert">
          {error}
        </p>
      )}
      {tasks.error != null && (
        <p className="text-danger mb-2 text-sm" role="alert">
          {describeError(tasks.error)}
        </p>
      )}
      {tasks.isPending && (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading tasks…
        </p>
      )}
      {shown?.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {kind === 'all' ? 'No tasks.' : `No ${kind} tasks.`}
        </p>
      )}
      <ul className={`flex flex-col gap-2 ${listClassName}`} data-testid="tasks-list">
        {shown?.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            onStatusChange={(status) => setStatus.mutate({ task: t, status })}
            canToggle={canWrite || t.assignee?.id === session.me.user.id}
            canDelete={canWrite || t.createdBy?.id === session.me.user.id}
            className="border-border bg-card rounded-xl border p-3"
          />
        ))}
      </ul>
    </section>
  );
}

/** "Priya Shah" → "PS": the assignee's avatar on a narrow task card. */
function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/**
 * One task line, shared with My Tasks. The checkbox is the shortcut between To do and Done
 * (TS-1739: unticking always lands on To do); the picker reaches In progress too. Who may use
 * either is `canToggle` — the assignee, or anyone who can write to the record. The title (and
 * the Maximize button, TS-1873) opens the task's detail (TS-1740), where the same people edit
 * it and `canDelete` may delete it; a task with a description also has a chevron that unfolds
 * the description under the title, in place.
 *
 * The row lays itself out by ITS OWN width (a container query, like the record pages): from
 * `@md` (28rem) up — My Tasks — title and meta on the left, the Overdue badge and the status
 * picker on the right; narrower — the record sidebar, the side panel — every card is the same
 * two lines: the title (medium weight; the description icon is left to the chevron), then one
 * meta line (wrapping only when it would run past the card), the status as a compact pill,
 * "Overdue" folded into the due date and the assignee as an initials avatar at the right edge
 * (the name is its tooltip). One grid,
 * one copy of each control: the meta line is `display: contents` when wide, so its children
 * take their own grid cells. The chevron and Maximize buttons have their own column at the
 * right (top-aligned like a note card's), so they never wrap onto a line of their own under a
 * long title. `className` replaces the row's padding: the record sidebar
 * draws each task as a card, like its notes; My Tasks keeps plain rows in a divided list.
 */
export function TaskRow({
  task,
  onStatusChange,
  canToggle,
  canDelete,
  showRecord = false,
  showAssignee = !showRecord,
  className = 'px-4 py-3',
}: {
  task: TaskView;
  onStatusChange: (next: TaskStatus) => void;
  canToggle: boolean;
  canDelete: boolean;
  showRecord?: boolean;
  /** Defaults to the opposite of `showRecord`; Team Tasks shows both. */
  showAssignee?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const done = task.status === 'done';
  const locked = statusLocked(task);
  const toggle = canToggle && !locked;
  const overdue = !done && task.dueAt !== null && new Date(task.dueAt).getTime() < Date.now();
  return (
    <li className={`@container text-sm ${className}`} data-testid="task" data-status={task.status}>
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1.5 @md:gap-y-1 @md:grid-cols-[auto_minmax(0,1fr)_auto_auto_auto]">
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground mt-0.5 disabled:opacity-40"
          aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
          title={
            locked
              ? 'Its run is over, so this task stays done.'
              : task.automated && !done && runPending(task)
                ? 'Closing this task cancels its send.'
                : undefined
          }
          disabled={!toggle}
          onClick={() => onStatusChange(done ? 'todo' : 'done')}
        >
          {done ? <CheckSquare className="size-4" /> : <Square className="size-4" />}
        </button>
        <div className={done ? 'text-muted-foreground line-through' : ''}>
          {/* The icon sits inside the button so it wraps with the title's last word; it is
            hidden from the button's name and announced by the text after the button. */}
          <button
            type="button"
            className="text-left font-medium hover:underline @md:font-normal"
            onClick={() => setOpen(true)}
            data-testid="task-open"
          >
            {task.title}
            {task.description && (
              <TextAlignStart
                className="text-muted-foreground ml-1.5 hidden size-3.5 align-[-2px] @md:inline"
                aria-hidden
                data-testid="task-has-description"
              />
            )}
          </button>
          {task.description && <span className="sr-only">Has a description</span>}
        </div>
        <span className="col-start-3 row-start-1 -mt-0.5 flex gap-1 @md:col-start-5 @md:row-span-2 @md:self-center">
          {/* Wide rows keep the chevron's slot, so status and View line up from row to row. */}
          {!task.description && <span className="hidden size-6 @md:block" aria-hidden />}
          {task.description && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={
                expanded ? `Hide description of ${task.title}` : `Show description of ${task.title}`
              }
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              data-testid="task-toggle"
            >
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            aria-label={`View ${task.title}`}
            onClick={() => setOpen(true)}
            data-testid="task-expand"
          >
            View
          </Button>
        </span>
        <div className="col-span-2 col-start-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs whitespace-nowrap @md:contents">
          {toggle ? (
            <Select value={task.status} onValueChange={(v) => onStatusChange(v as TaskStatus)}>
              <SelectTrigger
                className="h-6 w-auto shrink-0 gap-1 rounded-full px-2 text-xs @md:col-start-4 @md:row-span-2 @md:row-start-1 @md:self-center @md:h-7 @md:w-32 @md:rounded-md"
                aria-label={`Status of ${task.title}`}
                data-testid="task-status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TASK_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {TASK_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge
              variant={STATUS_BADGE[task.status]}
              className="@md:col-start-4 @md:row-span-2 @md:row-start-1 @md:self-center @md:justify-self-end"
              data-testid="task-status"
            >
              {TASK_STATUS_LABELS[task.status]}
            </Badge>
          )}
          {overdue && (
            <Badge
              variant="danger"
              className="hidden @md:col-start-3 @md:row-span-2 @md:row-start-1 @md:inline-flex @md:self-center"
            >
              Overdue
            </Badge>
          )}
          {/* Narrow: `contents`, so the pill, record, due date and assignee share ONE line (every
              card in the sidebar is the same two lines): the record truncates first, down to 4rem,
              and only then does the line wrap rather than run past the card. The assignee is an
              initials avatar at the right edge, the full name once the row is wide. */}
          <span className="text-muted-foreground contents text-xs @md:col-start-2 @md:row-start-2 @md:flex @md:flex-wrap @md:gap-x-3">
            {task.automated && (
              <Badge variant="brand" data-testid="task-automated">
                <Bot aria-hidden /> Automated
              </Badge>
            )}
            <AutomationBadge task={task} />
            {task.type && (
              <Badge variant="outline" data-testid="task-type">
                {TASK_TYPE_LABELS[task.type]}
              </Badge>
            )}
            {showRecord && (
              <span className="min-w-0 flex-[1_1_4rem] truncate @md:flex-none @md:overflow-visible">
                <TaskRecordLink task={task} />
              </span>
            )}
            {task.dueAt && (
              <span className={`shrink-0 ${overdue ? 'text-danger' : ''}`}>
                {overdue ? (
                  <>
                    <span className="@md:hidden">Overdue · </span>
                    <span className="hidden @md:inline">Due </span>
                  </>
                ) : (
                  'Due '
                )}
                {new Date(task.dueAt).toLocaleDateString()}
              </span>
            )}
            {showAssignee && task.assignee && (
              <span
                className="ml-auto shrink-0 @md:ml-0"
                title={task.assignee.name}
                data-testid="task-assignee"
              >
                <span
                  className="bg-brand-soft text-brand inline-flex size-5 items-center justify-center rounded-full text-[10px] font-semibold @md:hidden"
                  aria-hidden
                >
                  {initials(task.assignee.name)}
                </span>
                <span className="sr-only @md:not-sr-only">{task.assignee.name}</span>
              </span>
            )}
          </span>
        </div>
        {expanded && task.description && (
          <div
            className="border-border col-span-2 col-start-2 mt-1 border-l-2 pl-2"
            data-testid="task-description-inline"
          >
            <Markdown>{task.description}</Markdown>
          </div>
        )}
      </div>
      {open && (
        <TaskDetailDialog
          task={task}
          canEdit={canToggle}
          canDelete={canDelete}
          onClose={() => setOpen(false)}
        />
      )}
    </li>
  );
}
